// Help / FAQ content. Every answer describes screens, tabs and buttons that exist in the app
// today (labels copied from the code). `perms` are permission keys from utils/permissions.js:
// a user sees an entry only when they hold all of them. `roles` limits an entry to named roles
// (used for the parent portal, which has its own shell and no Topbar).
//
// Keep each answer to three short steps. If a feature is removed or renamed, update or delete
// its entry here; do not leave stale help behind.

const faq = (topic, q, steps, perms = [], roles = null) => ({ topic, q, steps, perms, roles });

export const FAQS = [
  // ── Fees
  faq('Fees', 'How do I generate the monthly challans?', [
    'Fees → Generate → New period. Set Months billed, Due date and the fee heads, then click Generate challans (this makes a draft).',
    'Check the summary and the "to check" list, then click Approve. Nothing reaches the books until you approve.',
    'Click Publish, then Print challans.',
  ], ['fees.view', 'fees.challan.generate']),

  faq('Fees', 'How do I add a one-off charge (for example annual charges) to every student?', [
    'Fees → Generate → New period.',
    'Under "One-off charge for every billed student", click Add one-off charge and enter the name and amount.',
    'Click Generate challans. To charge one student only, use Fees → Fee Assignment instead.',
  ], ['fees.view', 'fees.challan.generate']),

  faq('Fees', 'How do I print challans?', [
    'Fees → Collection. Tick the families, or tick none to print all.',
    'Click Print N (or Print all) at the top of the table.',
    'Use the print window to print. Print challans on the Generate tab does the same once a period is approved.',
  ], ['fees.view']),

  faq('Fees', 'How do I record a payment?', [
    'Fees → Collection → click Collect on the family row.',
    'Tick the students or charges being paid, type Cash received, pick the Method and Date.',
    'Click Record. Less than the total leaves the rest unpaid; extra money is kept as the family advance.',
  ], ['fees.view', 'fees.challan.collect']),

  faq('Fees', 'How do I take an advance payment from a family?', [
    'Fees → Collection. Families with nothing owed show an Advance button.',
    'Click Advance and enter the amount and method.',
    'The advance is applied automatically the next time you record a payment for that family (tick "Apply advance balance").',
  ], ['fees.view', 'fees.advance.deposit']),

  faq('Fees', 'How are arrears handled?', [
    'Unpaid amounts from earlier periods show in the Arrears column on Fees → Collection and in the payment window.',
    'Record payments as usual; the oldest charges are cleared first.',
    'A closed period stays collectable: its unpaid amounts remain as arrears.',
  ], ['fees.view']),

  faq('Fees', 'How do I add a late fine?', [
    'Fees → Generate, open the published period.',
    'Click Apply late fines, set the Fine per student and tick the students.',
    'Click Apply. Each fine is its own charge and can be waived later from the family ledger.',
  ], ['fees.view', 'fees.challan.generate']),

  faq('Fees', 'How do I close a period so I can start next month?', [
    'Fees → Generate and open the published period. Add late fines first if you need them.',
    'Click Close period and confirm. The New period button is locked until the open period is closed.',
    'After closing, no new charges can be added to that period.',
  ], ['fees.view', 'fees.challan.generate']),

  faq('Fees', 'How do I reverse a payment taken by mistake?', [
    'Fees → Collection → click the family name to open the ledger.',
    'Under Payments, click Reverse next to the receipt.',
    'Enter the reason and confirm. The amount goes back to outstanding.',
  ], ['fees.view', 'fees.payment.reverse']),

  faq('Fees', 'How do I void or waive a charge?', [
    'Fees → Collection → click the family name to open the ledger.',
    'Tick the charges, then click Void (charge was wrong) or Waive (forgive part or all).',
    'Enter a reason and confirm. A charge that already has payments must have those payments reversed first.',
  ], ['fees.view', 'fees.void']),

  faq('Fees', 'How do I charge one student a special fee?', [
    'Fees → Fee Assignment.',
    'Search the student, enter the fee head amounts or a custom charge, and add a note with the reason.',
    'Save. Approved charges attach automatically when challans are generated.',
  ], ['fees.view', 'fees.assign']),

  faq('Fees', 'Where do I set up fee heads and the fee structure?', [
    'Settings → Fees.',
    'Use the Fee Heads and Fee Structure sub-tabs.',
    'Challan & Due Date, Challan Design, Bank Details, Payment Methods and WhatsApp templates are in the same place.',
  ], ['settings.view', 'fees.view']),

  // ── Students & families
  faq('Students', 'How do I admit a new student?', [
    'Students → Add Student (or Admission under Quick access on the Dashboard).',
    'Fill in the form; the profile has General, Family, Finance, Health, Assessment, History and Attachments sections.',
    'Save.',
  ], ['students.view', 'students.add']),

  faq('Students', 'How do I edit a student?', [
    'Students → find the student in the list.',
    'Open the profile (or use the edit action on the row) and change the details.',
    'Save.',
  ], ['students.view', 'students.edit']),

  faq('Students', 'How do family IDs work?', [
    'Each family has one Family ID, shared by all its children, and challans are issued per family.',
    'Choose or create the family in the student\'s Family section when you admit the student.',
    'The Families page lists every family with its students.',
  ], ['families.view']),

  faq('Students', 'How do I promote students to the next class?', [
    'Settings → Bulk Update → Promotion.',
    'Choose the students and the destination class and section, then click Preview Promotion.',
    'Click Promote N Students. For more than 50 students you must type PROMOTE to confirm.',
  ], ['settings.view', 'students.promotion']),

  faq('Students', 'How do I import students from Excel?', [
    'Settings → Import & Export → Students. Download the template first.',
    'Fill it in, then upload the file and map the columns.',
    'Review the result for rows that were skipped or rejected.',
  ], ['settings.view']),

  faq('Students', 'Where do I keep a student\'s documents?', [
    'Students → open the profile → Attachments to upload files.',
    'Documents (in the menu) prints documents for the students you select, from templates.',
    'The Students page filter "Missing Documents" lists who is incomplete.',
  ], ['students.view']),

  // ── Attendance
  faq('Attendance', 'How do I mark daily attendance?', [
    'Attendance → pick the class and section.',
    'Mark each student Present, Absent, Late or Leave, or use All Present / All Absent first.',
    'Click Save.',
  ], ['attendance.view', 'attendance.edit']),

  faq('Attendance', 'How do I mark staff attendance?', [
    'Staff → Attendance tab and pick the date.',
    'Mark each person Present, Absent, Late or Leave, or use All Present / All Absent first.',
    'Click Save.',
  ], ['staff.view']),

  // ── POS & inventory
  faq('POS & Inventory', 'How do I make a sale at the counter?', [
    'POS → select the student, then click the products to add them to the cart.',
    'Type the amount received, or leave it blank to put the whole amount on credit.',
    'Complete the sale. A receipt is generated. Past sales are on the Sales tab.',
  ], ['pos.view', 'pos.sell']),

  faq('POS & Inventory', 'What happens to a sale on credit?', [
    'If the amount received is less than the total, the unpaid part is credited to the student\'s account.',
    'The POS screen shows how much is on credit before you complete the sale.',
    'Credit balances are collected with the fees, not at the POS.',
  ], ['pos.view', 'pos.sell']),

  faq('POS & Inventory', 'How do I add a product?', [
    'Inventory → Products → Add Product.',
    'Enter the name, prices, stock and minimum stock level, and mark it saleable for the POS.',
    'Save.',
  ], ['inventory.view']),

  faq('POS & Inventory', 'How do I order and receive stock?', [
    'Inventory → Purchase Orders → New PO to order from a vendor.',
    'When the goods arrive, Inventory → GRN → New Purchase to receive them into stock.',
    'Stock levels update and the amount is added to the vendor payable.',
  ], ['inventory.view']),

  faq('POS & Inventory', 'How do I manage vendors and what I owe them?', [
    'Inventory → Vendors → Add Vendor.',
    'Use a vendor\'s row actions to see the ledger and record a payment.',
    'The Dashboard card "Vendor payables" shows the total owed.',
  ], ['inventory.view']),

  // ── Finance
  faq('Finance', 'How do I record an expense?', [
    'Finance → Daily Voucher → New voucher.',
    'Choose the category, enter the lines and amounts, and save.',
    'Some vouchers need approval before they count.',
  ], ['expenses.view']),

  faq('Finance', 'Where are budgets, recurring bills and reports?', [
    'Finance has tabs for Recurring, Categories, Budget, Vendors, Salary and Reports.',
    'Use Recurring to set expenses that repeat each month.',
    'Use Overview and Ledger for the running totals.',
  ], ['expenses.view']),

  // ── Exams, timetable, diary, complaints
  faq('Academics', 'How do I schedule an exam and enter marks?', [
    'Exams → Schedule to create the exam.',
    'Exams → Mark Entry to enter each student\'s marks.',
    'Exams → Results to review them. Date Sheet and Paper Maker are separate tabs.',
  ], ['exams.view']),

  faq('Academics', 'How do I build a timetable?', [
    'Timetable → set up the periods and classes.',
    'Place teachers into the grid, or use the auto-fill option.',
    'Publish it when it is ready.',
  ], ['timetable.view']),

  faq('Academics', 'How do I write the daily diary?', [
    'Diary → choose the class section.',
    'Enter the diary for each subject for the day.',
    'Save. Parents can read it in the parent portal.',
  ], ['diary.view']),

  faq('Academics', 'How do I handle a complaint?', [
    'Complaints lists every ticket; filter by status such as Escalated or Resolved.',
    'Open a ticket and change its status as you work on it.',
    'To close it, resolve it and write the resolution text (required).',
  ], ['complaints.view']),

  // ── Staff & payroll
  faq('Staff', 'How do I add a staff member?', [
    'Staff → Staff List → Add Staff.',
    'Fill in the details, including salary and join date.',
    'Save. If they need to sign in, use Generate login for that person.',
  ], ['staff.view']),

  faq('Staff', 'How do I run payroll?', [
    'Staff → Payroll, choose the month.',
    'Click Generate Payroll. Advances and deductions are applied automatically.',
    'Click Mark Paid for each person. Print their slip with Print Salary Slip, or from the Salary Slips tab.',
  ], ['staff.view']),

  // ── Settings
  faq('Settings', 'How do I create a user and what do they sign in with?', [
    'Settings → Users & Roles → Directory → Create user (admins only).',
    'Pick the role. Staff logins are built as aihs.<name>@login.internal, so you type only the middle part; Owner, Director and Admin can use a real email instead.',
    'Generate a password and share it. The user must change it at first login.',
  ], ['settings.view', 'users_roles.edit']),

  faq('Settings', 'How do I change what a role can do?', [
    'Settings → Users & Roles → Permission Matrix.',
    'Pick the role and tick or untick permissions.',
    'Click Save changes. Owner permissions cannot be edited.',
  ], ['settings.view', 'users_roles.edit']),

  faq('Settings', 'How do I change the school details or the colours?', [
    'Settings → School Info for the name, contact details and logo.',
    'Settings → Display & Theme to pick a colour palette for the whole app.',
    'Click Save All.',
  ], ['settings.view']),

  // ── Dashboard
  faq('Dashboard', 'What do the Dashboard cards mean?', [
    'The top cards show the month\'s fees collected, outstanding (arrears plus this month), families still owing, active students, staff, and vendor payables. Click a card to open its page.',
    'The month-money card shows money in, money out and the net cash for the month.',
    '"Needs attention" under the fee chart lists what to act on today.',
  ], ['dashboard.view']),

  faq('Dashboard', 'How do I choose the month, or refresh the numbers?', [
    'Use the Period selector in the header for an earlier month.',
    'Use the refresh button next to it to reload.',
    'The Campus selector shows your current school.',
  ], ['dashboard.view']),

  faq('Dashboard', 'How do I change the Quick access shortcuts?', [
    'On the Dashboard, click the gear icon in the Quick access card.',
    'Tick up to six actions and use the arrows to reorder them.',
    'Click Save. The choice is remembered for you on this browser.',
  ], ['dashboard.view']),

  faq('Dashboard', 'How do I collect from a family on the Dashboard?', [
    'In "Top outstanding families", click Pay on the family row.',
    'Enter the cash received in the payment window and record it.',
    'The Send icon opens a WhatsApp reminder for that family.',
  ], ['dashboard.view', 'fees.challan.collect']),

  // ── Parent portal (shown only inside the parent portal, which has no Topbar)
  faq('Parent portal', 'How do I see what my family owes?', [
    'Open the Home tab after signing in.',
    'The family balance shows "All clear" or the amount due, with the next due date.',
    'The Spending tab shows what your children bought at the school shop.',
  ], [], ['parent']),

  faq('Parent portal', 'Where can I see attendance, diary and results?', [
    'Use the Attendance, Diary and Academics tabs.',
    'Choose the child if you have more than one.',
    'The Documents tab is for uploading documents, and Complaints is for raising an issue with the school.',
  ], [], ['parent']),
];

// What this user is allowed to see. Entries with `roles` are only for those roles; everything
// else needs every listed permission.
export function faqsFor(user, hasPermission) {
  const role = user?.roleName || '';
  return FAQS.filter(f => {
    if (f.roles) return f.roles.includes(role);
    return f.perms.every(p => hasPermission(p));
  });
}
